import { forwardRef, useState } from 'react';
import { ReactComponent as HidePassword } from '../../Assets/closed-eye.svg';
import { ReactComponent as ShowPassword } from '../../Assets/opened-eye.svg';
import Button from '../Button';
import Text from '../Text';
import DateInput from './dateInput';
import InputContainerWrapper from './style/inputContainerWrapper';
import InputWrapper from './style/inputWrapper';
import LabelWrapper from './style/labelWrapper';
import Switcher from './switcher';

const validInputTypes = Object.freeze([
  'text',
  'number',
  'password',
  'tel',
  'email',
  'url',
  'time',
  'date',
  'checkbox',
]);

const Input = forwardRef((props, ref) => {
  const { type, label, className, error, value, hideButtons, step } = props;
  const [showPassword, setShowPassword] = useState(false);

  /// variables
  const isTypeValid = validInputTypes.includes(type);
  const validType = isTypeValid ? type : validInputTypes[0];
  const isPassword = type === 'password';
  const isTelephone = type === 'tel';
  const isNumeric = type === 'number';
  const isSwitch = type === 'checkbox';
  const handledType = showPassword && isPassword ? 'text' : validType;
  const handledClassName = `${isPassword ? 'password-field' : ''}${isTelephone ? 'phone-field' : ''} ${className ? `${className}` : ''}`;
  const needWrapper = label || isPassword || isTelephone || isNumeric || error;

  /// functions
  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const createNumericChanger = (direction) => () => {
    if (!isNumeric) return;

    const stepValue = parseFloat(step) || 1;
    let newValue = (parseFloat(value) || 0) + stepValue * direction;

    if (props.min !== undefined) newValue = Math.max(newValue, props.min);
    if (props.max !== undefined) newValue = Math.min(newValue, props.max);

    props.onChange?.({ target: { name: props.name, value: newValue } });
  };

  const increment = createNumericChanger(1);
  const decrement = createNumericChanger(-1);

  /// rendering elements
  const Wrapper = label ? LabelWrapper : InputContainerWrapper;

  const inputWithWrapper = (
    <Wrapper
      $styleType={handledType}
      className={handledClassName}
      $preventLabelClick={isNumeric}
    >
      {label && (
        <Text size='SM' className='label-text'>
          {label}
        </Text>
      )}

      <div
        className={`input-field-with-icon-container ${isNumeric ? 'numeric-field' : ''}`}
      >
        {isNumeric && !hideButtons && (
          <button type='button' className='minus-btn' onClick={decrement}>
            -
          </button>
        )}

        <InputWrapper
          {...props}
          className={`${isPassword ? 'password-field' : ''}${isTelephone ? 'phone-field' : ''}`}
          $styleType={handledType}
          type={handledType}
          ref={ref}
        />

        {isNumeric && !hideButtons && (
          <button type='button' className='plus-btn' onClick={increment}>
            +
          </button>
        )}

        {isPassword && (
          <Button
            type='icon'
            className='show-hide-icon'
            onClick={togglePasswordVisibility}
            icon={showPassword ? <HidePassword /> : <ShowPassword />}
          />
        )}

        {isTelephone && <span className='number-prefix'>+374</span>}
      </div>

      {error && (
        <Text size='SM' className='error-message'>
          {error}
        </Text>
      )}
    </Wrapper>
  );

  if (type === 'date') {
    return (
      <DateInput
        {...props}
        value={value}
        onChange={props.onChange}
        label={label}
        error={error}
        ref={ref}
      />
    );
  }
  if (isSwitch) {
    return <Switcher {...props} ref={ref} />;
  }

  return needWrapper ? (
    inputWithWrapper
  ) : (
    <InputWrapper
      {...props}
      className={handledClassName}
      $styleType={handledType}
      type={handledType}
      ref={ref}
    />
  );
});

Input.displayName = 'Input';
export default Input;
